import { Link } from 'react-router-dom';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { articles } from '@/mocks/articles';
import { categoryBadgeClass, formatViews } from '@/utils/format';

export default function FeaturedSection() {
  const featured = articles.filter((item) => item.featured);
  const [lead, ...rest] = featured;

  if (!lead) return null;

  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <SectionHeading
        eyebrow="Editor's Pick"
        title="本周精选观察"
        desc="从当期最值得聊的几件事入手，先把来龙去脉讲清楚，再谈立场。"
        action={
          <Link
            to="/topics"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            查看全部话题
            <i className="ri-arrow-right-line"></i>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <article className="group h-full overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
            <Link to={`/article/${lead.slug}`} className="block">
              <div className="relative h-64 w-full overflow-hidden md:h-80">
                <img
                  src={lead.cover}
                  alt={lead.title}
                  title={`${lead.title} - 美日吃瓜`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  本周封面
                </span>
              </div>
            </Link>
            <div className="p-6 md:p-7">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Link
                  to={`/category/${lead.category}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${categoryBadgeClass(lead.category)}`}
                >
                  {lead.categoryName}
                </Link>
                <span className="text-xs text-foreground-500">{lead.date}</span>
              </div>
              <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                <Link to={`/article/${lead.slug}`} className="transition-colors hover:text-primary-600">
                  {lead.title}
                </Link>
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{lead.summary}</p>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {lead.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约 {lead.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(lead.views)} 次围观
                </span>
              </div>
            </div>
          </article>
        </Reveal>

        <div className="flex flex-col gap-6 lg:col-span-5">
          {rest.slice(0, 2).map((item, index) => (
            <Reveal key={item.slug} delay={index * 90}>
              <article className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300">
                <Link to={`/article/${item.slug}`} className="flex flex-col sm:flex-row">
                  <div className="relative h-44 w-full shrink-0 overflow-hidden sm:h-auto sm:w-40">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 美日吃瓜`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span
                      className={`mb-3 w-fit rounded-full border px-3 py-1 text-xs font-medium ${categoryBadgeClass(item.category)}`}
                    >
                      {item.categoryName}
                    </span>
                    <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950">
                      <span className="transition-colors group-hover:text-primary-600">{item.title}</span>
                    </h3>
                    <p className="line-clamp-2 mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                    <span className="mt-4 flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-time-line"></i>
                      {item.date}
                    </span>
                  </div>
                </Link>
              </article>
            </Reveal>
          ))}

          <Reveal delay={180}>
            <div className="flex h-full flex-col justify-center rounded-lg border border-accent-200 bg-accent-50 p-6">
              <i className="ri-lightbulb-flash-line text-2xl text-accent-700"></i>
              <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">围观小贴士</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                遇到立场激烈的争议时，先确认三件事：事件时间线、发言者身份、被截取的那句话原本的上下文。这三步能过滤掉大部分情绪噪音。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex w-fit items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-800 transition-colors hover:text-accent-700"
              >
                了解我们的内容原则
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}